import { Box, Checkbox, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import React from 'react';
import groupBy from 'lodash/groupBy';
import { TextInput } from '../TextInput';

export type BaseListBoxOption = {
  id: string;
};

export type ListBoxProps<Option extends BaseListBoxOption = BaseListBoxOption> = {
  name: string;
  options: Option[];
  /**
     * Used to determine the label for a given option.
    */
  getOptionLabel: (option: Option) => string;
  /**
     * Used to determine the disabled state for a given option.
     */
  getOptionDisabled?: (option: Option) => boolean;
  /**
     * Used to determine the variant to render the options as.
     */
  optionVariant?: 'checkbox' | 'default';


  color?: 'primary' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  groupBy?: (opt: Option) => string;
  emptyMessage?: React.ReactNode;
  disableListPadding?: boolean;

  other?: {
    name: string;
    isOtherItem: (option: Option) => boolean;
    showInput: boolean;
  };
};

export const ListBox = <Option extends BaseListBoxOption>({ color = 'secondary', optionVariant = 'checkbox', ...props }: ListBoxProps<Option>) => {
  const [ field, _meta, { setValue, setTouched } ] = useField<string[]>(props.name);
  const { classes, cx } = useListBoxStyles();
  const selectedIds = React.useMemo(() => field.value ?? [], [ field.value ]);

  const onSelectValue = React.useCallback((value: BaseListBoxOption, select: boolean) => {
    if (select) {
      setValue([ ...selectedIds, value.id ], true);
    } else {
      setValue(selectedIds.filter(id => id !== value.id), true);
    }

    setTouched(true, false);
  }, [ selectedIds, setTouched, setValue ]);

  const groups = React.useMemo(() => {
    if (props.groupBy) {
      const grouped = groupBy(props.options, props.groupBy);

      return Object.keys(grouped).map(title => ({ title, items: grouped[title] }) );
    }
    return [ { title: '', items: props.options } ];
  }, [ props.options, props.groupBy ]);

  return (
    <Box className={classes.listBox}>
      <List component="ul" disablePadding={!props.options.length || props.disableListPadding} className={classes.list}>
        {groups.map(group => (
          <React.Fragment key={group.title}>
            {group.title ? <Typography sx={{ mt: 1 }} variant="overline" component="h2">{group.title}</Typography> : null}
            {group.items.map((option) => {
              const selected = selectedIds.includes(option.id);
              const label = props.getOptionLabel(option);
              const disabled = props.disabled || props.getOptionDisabled?.(option);
              const isOther = props.other?.isOtherItem(option) ?? false;

              return (
                <ListItemButton
                  key={option.id}
                  selected={selected}
                  disableRipple
                  component="li"
                  aria-selected={selected}
                  className={cx(classes.listItem, {
                    [classes.checkboxListItem]: optionVariant === 'checkbox',
                    [classes.listItemPrimary]: color === 'primary'
                  })}
                  onClick={isOther ? undefined : () => onSelectValue(option, !selected)}
                  disabled={disabled}
                >
                  {optionVariant === 'checkbox' && (
                    <Checkbox
                      color={color}
                      checked={selected}
                      disableRipple
                      disabled={disabled}
                      onClick={isOther ? () => onSelectValue(option, !selected) : undefined}
                    />
                  )}
                  {props.other && props.other.showInput && props.other.isOtherItem(option) ? (
                    <TextInput autoFocus placeholder="Other" size="small" name={props.other.name} fullWidth color="secondary" />
                  ) : (
                    <ListItemText>{label}</ListItemText>
                  )}
                </ListItemButton>
              );
            })}
          </React.Fragment>
        ))}
      </List>
      {props.options.length === 0 && (
        <Box className={classes.emptyListContainer}>
          {!props.loading && (
            <Typography variant="body2" className={classes.emptyListText}>
              {props.emptyMessage}
            </Typography>
          )}
          {props.loading && <CircularProgress color={color} />}
        </Box>
      )}
    </Box>
  );
};

const useListBoxStyles = makeStyles({ name: 'ListBox' })(theme => ({
  listBox: {
    height: '100%',
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    '&::-webkit-scrollbar': {
      backgroundColor: theme.palette.grey[100],
      borderLeft: `1px solid ${theme.palette.divider}`,
      width: '14px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderWidth: '4px',
      borderRadius: '8px'
    }
  },
  list: {
    background: theme.palette.common.white,
  },
  listItem: {
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.secondary.background,
      '&:hover': {
        backgroundColor: theme.palette.secondary.background,
      }
    }
  },
  listItemPrimary: {
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.primary.background,
      '&:hover': {
        backgroundColor: theme.palette.primary.background,
      }
    }
  },
  checkboxListItem: {
    padding: 0
  },
  emptyListContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${theme.spacing(5)} 0`
  },
  emptyListText: {
    maxWidth: '410px',
    textAlign: 'center',
    color: theme.palette.text.hint
  }
}));
